import React, { useState } from 'react';
import { DayPicker } from 'react-day-picker';
import { format } from 'date-fns';
import { da } from 'date-fns/locale';
import { Calendar, Clock, User, Mail, Phone, FileText } from 'lucide-react';
import { products } from '../stripe-config';
import { createBookingCheckoutSession } from '../lib/stripe';
import { getAvailableDates, isDateAvailable, getAvailableTimesForDate, organizeTimesByPeriod } from '../lib/availability';
import "react-day-picker/dist/style.css";

interface BookingFormProps {
  language: string;
}

const BookingForm: React.FC<BookingFormProps> = ({ language }) => {
  const [selectedDate, setSelectedDate] = useState<Date>();
  const [selectedTime, setSelectedTime] = useState<string>('');
  const [selectedService, setSelectedService] = useState<string>('');
  const [selectedLocation, setSelectedLocation] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [availableDates, setAvailableDates] = useState<string[]>([]);
  const [availableTimes, setAvailableTimes] = useState<string[]>([]);
  const [loadingAvailability, setLoadingAvailability] = useState(false);

  const [customerData, setCustomerData] = useState({
    name: '',
    email: '',
    phone: '',
    notes: ''
  });

  const locations = [
    { value: 'aarhus', label: 'Thorvaldsensgade 13, 8000 Aarhus' }
  ];

  // Add error boundary for this component
  const [hasError, setHasError] = useState(false);

  if (hasError) {
    return (
      <div className="max-w-4xl mx-auto p-6 relative z-10">
        <div className="text-center mb-8">
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-white mb-2">
            Booking Temporarily Unavailable
          </h1>
          <p className="text-gray-300">
            Please try again later or contact us directly.
          </p>
        </div>
      </div>
    );
  }

  const content = {
    en: {
      title: "Book Your Appointment",
      subtitle: "Select your service, date, and time",
      form: {
        service: "Select Service",
        selectService: "Choose a service",
        location: "Select Location",
        selectLocation: "Choose a location",
        date: "Select Date",
        time: "Select Time",
        customerInfo: "Customer Information",
        name: "Full Name",
        email: "Email Address",
        phone: "Phone Number",
        notes: "Additional Notes (Optional)",
        submit: "Proceed to Payment"
      },
      timeSlots: {
        morning: "Morning",
        afternoon: "Afternoon",
        evening: "Evening"
      },
      validation: {
        selectService: "Please select a service",
        selectDate: "Please select a date",
        selectTime: "Please select a time",
        enterName: "Please enter your full name",
        enterEmail: "Please enter a valid email address",
        enterPhone: "Please enter your phone number"
      },
      consultation: {
        title: "Free Consultation",
        deposit: "Booking Deposit: 200 DKK",
        benefits: [
          "Fully refundable upon visit",
          "100% deducted from treatment cost",
          "Complete refund with 24h cancellation notice",
          "Secures your exclusive consultation time"
        ],
        note: "A small deposit of 200 DKK is required to confirm your appointment and ensure commitment. This deposit reserves your dedicated consultation time."
      }
    },
    da: {
      title: "Book Din Tid",
      subtitle: "Vælg din service, dato og tid",
      form: {
        service: "Vælg Service",
        selectService: "Vælg en service",
        location: "Vælg Lokation",
        selectLocation: "Vælg en lokation",
        date: "Vælg Dato",
        time: "Vælg Tid",
        customerInfo: "Kundeinformation",
        name: "Fulde Navn",
        email: "E-mailadresse",
        phone: "Telefonnummer",
        notes: "Yderligere Bemærkninger (Valgfrit)",
        submit: "Fortsæt til Betaling"
      },
      timeSlots: {
        morning: "Morgen",
        afternoon: "Eftermiddag",
        evening: "Aften"
      },
      validation: {
        selectService: "Vælg venligst en service",
        selectDate: "Vælg venligst en dato",
        selectTime: "Vælg venligst et tidspunkt",
        enterName: "Indtast venligst dit fulde navn",
        enterEmail: "Indtast venligst en gyldig e-mailadresse",
        enterPhone: "Indtast venligst dit telefonnummer"
      },
      consultation: {
        title: "Gratis Konsultation",
        deposit: "Booking Depositum: 200 DKK",
        benefits: [
          "Fuldt refunderbart ved fremmøde",
          "100% fratrækkes behandlingsomkostninger",
          "Fuld refusion ved 24t aflysningsvarsel",
          "Sikrer din eksklusive konsultationstid"
        ],
        note: "Et lille depositum på 200 DKK er påkrævet for at bekræfte din aftale og sikre engagement. Dette depositum reserverer din dedikerede konsultationstid."
      }
    }
  };

  // Load available slots on component mount
  React.useEffect(() => {
    try {
      loadAvailableDates();
    } catch (error) {
      console.error('Error loading availability:', error);
      setHasError(true);
    }
  }, []);

  // Load available times when date is selected
  React.useEffect(() => {
    try {
      if (selectedDate) {
        loadTimesForSelectedDate(selectedDate);
      } else {
        setAvailableTimes([]);
        setSelectedTime('');
      }
    } catch (error) {
      console.error('Error loading times:', error);
      setHasError(true);
    }
  }, [selectedDate]);

  const loadAvailableDates = async () => {
    setLoadingAvailability(true);
    try {
      const dates = await getAvailableDates();
      setAvailableDates(dates);
    } catch (err) {
      console.error('Error loading available dates:', err);
      setAvailableDates([]);
    } finally {
      setLoadingAvailability(false);
    }
  };

  const loadTimesForSelectedDate = async (date: Date) => {
    setLoadingAvailability(true);
    try {
      const times = await getAvailableTimesForDate(date);
      setAvailableTimes(times);
      
      // Clear selected time if it's no longer available
      if (selectedTime && !times.includes(selectedTime)) {
        setSelectedTime('');
      }
    } catch (err) {
      console.error('Error loading times for date:', err);
      setAvailableTimes([]);
    } finally {
      setLoadingAvailability(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setCustomerData(prev => ({ ...prev, [name]: value }));
  };

  const validateForm = (): string | null => {
    const langContent = content[language as keyof typeof content];
    
    if (!selectedService) return langContent.validation.selectService;
    if (!selectedLocation) return langContent.validation.selectLocation || 'Please select a location';
    if (!selectedDate) return langContent.validation.selectDate;
    if (!selectedTime) return langContent.validation.selectTime;
    if (!customerData.name.trim()) return langContent.validation.enterName;
    if (!customerData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customerData.email)) {
      return langContent.validation.enterEmail;
    }
    if (!customerData.phone.trim()) return langContent.validation.enterPhone;
    
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      setIsLoading(false);
      return;
    }

    try {
      // Find the selected product
      const product = products.find(p => p.priceId === selectedService);
      if (!product) {
        setError('Selected service not found');
        setIsLoading(false);
        return;
      }

      console.log('Creating booking checkout session with:', {
        price_id: selectedService,
        customer_email: customerData.email,
        customer_name: customerData.name,
        appointment_date: format(selectedDate!, 'yyyy-MM-dd'),
        appointment_time: selectedTime
      });

      // Create booking checkout session
      const { url } = await createBookingCheckoutSession({
        price_id: selectedService,
        success_url: `${window.location.origin}/success`,
        cancel_url: `${window.location.origin}/book`,
        mode: product.mode,
        customer_email: customerData.email,
        customer_name: customerData.name,
        customer_phone: customerData.phone,
        appointment_date: format(selectedDate!, 'yyyy-MM-dd'),
        appointment_time: selectedTime,
        customer_notes: customerData.notes || undefined,
        location: selectedLocation
      });

      console.log('Checkout session created, redirecting to:', url);

      // Redirect to Stripe checkout
      window.location.href = url;
    } catch (err) {
      console.error('Checkout error:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to start checkout process';
      console.error('Full error details:', err);
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const renderTimeSlots = () => {
    if (availableTimes.length === 0) {
      return (
        <div className="text-center py-8">
          <p className="text-white/70">
            {language === 'da' 
              ? 'Ingen tilgængelige tider for den valgte dato' 
              : 'No available times for the selected date'}
          </p>
        </div>
      );
    }

    const { morning, afternoon, evening } = organizeTimesByPeriod(availableTimes);
    const periods = [
      { key: 'morning', times: morning, label: content[language as keyof typeof content].timeSlots.morning },
      { key: 'afternoon', times: afternoon, label: content[language as keyof typeof content].timeSlots.afternoon },
      { key: 'evening', times: evening, label: content[language as keyof typeof content].timeSlots.evening }
    ].filter(period => period.times.length > 0);

    if (periods.length === 0) {
      return (
        <div className="text-center py-8">
          <p className="text-white/70">
            {language === 'da' 
              ? 'Ingen tilgængelige tider for den valgte dato' 
              : 'No available times for the selected date'}
          </p>
        </div>
      );
    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {periods.map(({ key, times, label }) => (
          <div key={key}>
            <h4 className="text-sm font-medium text-white mb-2">
              {label}
            </h4>
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {times.map(time => (
                <button
                  key={time}
                  type="button"
                  onClick={() => setSelectedTime(time)}
                  className={`w-full px-3 py-2 text-sm rounded-md transition-all duration-300 ${
                    selectedTime === time
                      ? 'bg-[#A68B7C] text-white shadow-lg'
                      : 'bg-white/10 text-white hover:bg-white/20 border border-white/20'
                  }`}
                >
                  {time}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="max-w-4xl mx-auto p-6 relative z-10">
      <div className="text-center mb-8">
        <h1 className="text-4xl md:text-5xl font-serif font-bold text-white mb-2" 
            style={{ 
              fontFamily: "'Playfair Display', serif",
              textShadow: '0 4px 8px rgba(0,0,0,0.4)',
              letterSpacing: '0.5px',
              lineHeight: '1.2'
            }}>
          {content[language as keyof typeof content].title}
        </h1>
        
        {/* Consultation Notice Box - Positioned after title */}
        {/* Striking Consultation Banner */}
        <div className="relative mb-8 max-w-3xl mx-auto group">
          {/* Main Banner Container */}
          <div className="relative bg-gradient-to-r from-[#2A2A2A] via-[#1A1A1A] to-[#2A2A2A] rounded-2xl p-8 shadow-2xl overflow-hidden border border-[#A68B7C]/20">
            {/* Animated Background Pattern */}
            <div className="absolute inset-0 opacity-5">
              <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-[#A68B7C]/10 to-transparent"></div>
              <div className="absolute top-4 left-4 w-32 h-32 border border-[#A68B7C]/20 rounded-full"></div>
              <div className="absolute bottom-4 right-4 w-24 h-24 border border-[#A68B7C]/15 rounded-full"></div>
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-48 h-48 border border-[#A68B7C]/10 rounded-full"></div>
            </div>
            
            {/* Metallic Border Effect */}
            <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-[#A68B7C]/30 via-[#B8967D]/20 to-[#A68B7C]/30 p-[1px]">
              <div className="w-full h-full bg-gradient-to-r from-[#2A2A2A] via-[#1A1A1A] to-[#2A2A2A] rounded-2xl"></div>
            </div>
            
            {/* Content Container */}
            <div className="relative z-10">
              {/* Main Banner Text */}
              <div className="text-center mb-6">
                <div className="inline-block relative">
                  {/* Decorative Lines */}
                  <div className="absolute -left-16 top-1/2 w-12 h-[1px] bg-gradient-to-r from-transparent to-[#A68B7C]/60"></div>
                  <div className="absolute -right-16 top-1/2 w-12 h-[1px] bg-gradient-to-l from-transparent to-[#A68B7C]/60"></div>
                  
                  {/* Main Title */}
                  <h2 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-[#D4B494] via-[#F5F0EC] to-[#D4B494] bg-clip-text text-transparent mb-2 tracking-wider transform group-hover:scale-105 transition-transform duration-300"
                      style={{ 
                        fontFamily: "'Inter', sans-serif",
                        fontWeight: '800',
                        textShadow: '0 4px 8px rgba(0,0,0,0.3)',
                        letterSpacing: '0.1em'
                      }}>
                    {language === 'da' ? 'GRATIS' : 'FREE'}
                  </h2>
                  
                  {/* Subtitle */}
                  <h3 className="text-2xl md:text-3xl font-serif text-[#F5F0EC]/90 tracking-wide"
                      style={{ 
                        fontFamily: "'Playfair Display', serif",
                        fontWeight: '400',
                        letterSpacing: '0.05em'
                      }}>
                    {language === 'da' ? 'KONSULTATION' : 'CONSULTATION'}
                  </h3>
                </div>
                
                {/* Deposit Information */}
                <div className="mt-6 px-6 py-3 bg-gradient-to-r from-[#A68B7C]/10 to-[#B8967D]/10 rounded-lg border border-[#A68B7C]/20">
                  <p className="text-[#F5F0EC] font-medium text-lg">
                    {content[language as keyof typeof content].consultation.deposit}
                  </p>
                </div>
              </div>
              
              {/* Benefits List */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                {content[language as keyof typeof content].consultation.benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start text-white/90 text-sm leading-relaxed bg-[#2A2A2A]/30 rounded-lg p-3 border border-[#A68B7C]/30">
                    <span className="text-[#A68B7C] mr-3 mt-0.5 flex-shrink-0 text-base">✦</span>
                    <span className="font-medium">{benefit}</span>
                  </div>
                ))}
              </div>
              
              {/* Note Section */}
              <div className="text-center pt-4 border-t border-[#A68B7C]/20">
                <p className="text-xs text-white/70 leading-relaxed max-w-2xl mx-auto">
                  <span className="font-semibold text-[#A68B7C]">{language === 'da' ? 'Bemærk:' : 'Note:'}</span> {content[language as keyof typeof content].consultation.note}
                </p>
              </div>
            </div>
            
            {/* Hover Effect Overlay */}
            <div className="absolute inset-0 bg-gradient-to-r from-[#A68B7C]/5 to-[#B8967D]/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-2xl"></div>
          </div>
          
          {/* Outer Glow Effect */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#A68B7C]/20 via-[#B8967D]/10 to-[#A68B7C]/20 rounded-2xl blur-xl opacity-30 group-hover:opacity-50 transition-opacity duration-500 -z-10"></div>
        </div>
        
        <p className="text-gray-300">
          {content[language as keyof typeof content].subtitle}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {error && (
          <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}

        {/* Service Selection */}
        <div className="bg-white/10 backdrop-blur-md rounded-lg border border-white/20 p-6">
          <div className="flex items-center gap-2 mb-4">
            <FileText className="w-5 h-5 text-[#A68B7C]" />
            <h3 className="text-lg font-semibold text-white">{content[language as keyof typeof content].form.service}</h3>
          </div>
          <select
            value={selectedService}
            onChange={(e) => setSelectedService(e.target.value)}
            className="w-full px-4 py-3 bg-[#2A2A2A] border border-white/20 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A68B7C]/50 focus:border-[#A68B7C]"
          >
            <option value="">{content[language as keyof typeof content].form.selectService}</option>
            {products.map((product) => (
              <option key={product.id} value={product.priceId}>
                {product.name} - {new Intl.NumberFormat('da-DK', {
                  style: 'currency',
                  currency: product.currency,
                  minimumFractionDigits: 0
                }).format(product.price / 100)}
              </option>
            ))}
          </select>
        </div>

        {/* Location Selection */}
        <div className="bg-white/10 backdrop-blur-md rounded-lg border border-white/20 p-6">
          <div className="flex items-center gap-2 mb-4">
            <svg className="w-5 h-5 text-rose-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <h3 className="text-lg font-semibold text-white">{content[language as keyof typeof content].form.location}</h3>
          </div>
          <select
            value={selectedLocation}
            onChange={(e) => setSelectedLocation(e.target.value)}
            className="w-full px-4 py-3 bg-[#2A2A2A] border border-white/20 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A68B7C]/50 focus:border-[#A68B7C] transition-all duration-300"
          >
            <option value="">{content[language as keyof typeof content].form.selectLocation}</option>
            {locations.map((location) => (
              <option key={location.value} value={location.value}>
                {location.label}
              </option>
            ))}
          </select>
        </div>

        {/* Date Selection */}
        <div className="bg-white/10 backdrop-blur-md rounded-lg border border-white/20 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Calendar className="w-5 h-5 text-[#A68B7C]" />
            <h3 className="text-lg font-semibold text-white">{content[language as keyof typeof content].form.date}</h3>
          </div>
          {loadingAvailability ? (
            <div className="text-center py-8">
              <div className="inline-flex items-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                <span className="text-white">
                  {language === 'da' ? 'Indlæser tilgængelighed...' : 'Loading availability...'}
                </span>
              </div>
            </div>
          ) : (
            <div className="[&_.rdp]:text-white [&_.rdp-button]:text-white [&_.rdp-day_today]:bg-rose-300/20 [&_.rdp-day_today]:text-rose-300">
                        <DayPicker
              mode="single"
              selected={selectedDate}
              onSelect={setSelectedDate}
              locale={language === 'da' ? da : undefined}
              disabled={(date) => {
                // Disable dates before today (midnight)
                const today = new Date();
                today.setHours(0, 0, 0, 0); // Set to midnight of today
                if (date < today) return true;
                // Disable dates not in available dates
                return !isDateAvailable(date, availableDates);
              }}
              className="mx-auto"
              modifiersStyles={{
                selected: { backgroundColor: '#A68B7C', color: 'white' },
                disabled: { opacity: 0.3, cursor: 'not-allowed' }
              }}
            />

            </div>
          )}
          </div>

        {/* Time Selection */}
        {selectedDate && (
          <div className="bg-white/10 backdrop-blur-md rounded-lg border border-white/20 p-6">
            <div className="flex items-center gap-2 mb-4">
              <Clock className="w-5 h-5 text-[#A68B7C]" />
              <h3 className="text-lg font-semibold text-white">{content[language as keyof typeof content].form.time}</h3>
            </div>
            {renderTimeSlots()}
          </div>
        )}

        {/* Customer Information */}
        {selectedTime && (
          <div className="bg-white/10 backdrop-blur-md rounded-lg border border-white/20 p-6">
            <div className="flex items-center gap-2 mb-4">
              <User className="w-5 h-5 text-[#A68B7C]" />
              <h3 className="text-lg font-semibold text-white">{content[language as keyof typeof content].form.customerInfo}</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-white mb-1">
                  {content[language as keyof typeof content].form.name}
                </label>
                <input
                  type="text"
                  name="name"
                  value={customerData.name}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-[#2A2A2A] border border-white/20 text-white placeholder-gray-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A68B7C]/50 focus:border-[#A68B7C]"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-white mb-1">
                  {content[language as keyof typeof content].form.phone}
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={customerData.phone}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-[#2A2A2A] border border-white/20 text-white placeholder-gray-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A68B7C]/50 focus:border-[#A68B7C]"
                  required
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-white mb-1">
                  {content[language as keyof typeof content].form.email}
                </label>
                <input
                  type="email"
                  name="email"
                  value={customerData.email}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-[#2A2A2A] border border-white/20 text-white placeholder-gray-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A68B7C]/50 focus:border-[#A68B7C]"
                  required
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-white mb-1">
                  {content[language as keyof typeof content].form.notes}
                </label>
                <textarea
                  name="notes"
                  value={customerData.notes}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-4 py-3 bg-[#2A2A2A] border border-white/20 text-white placeholder-gray-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A68B7C]/50 focus:border-[#A68B7C] resize-none"
                />
              </div>
            </div>
          </div>
        )}

        {/* Submit Button */}
        {selectedTime && selectedLocation && customerData.name && customerData.email && customerData.phone && (
          <div className="text-center">
            <button
              type="submit"
              disabled={isLoading || !selectedService || !selectedLocation || !selectedDate || !selectedTime || !customerData.name || !customerData.email || !customerData.phone}
              className="px-8 py-3 bg-[#A68B7C] text-white font-medium rounded-lg hover:bg-[#95796A] transition-all duration-300 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center mx-auto shadow-lg hover:shadow-xl transform hover:scale-[1.02]"
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Processing...
                </>
              ) : (
                content[language as keyof typeof content].form.submit
              )}
            </button>
          </div>
        )}
      </form>
    </div>
  );
};

export default BookingForm;